import { z } from 'zod';
import { buildCoverageMatrix, CoverageMatrixSchema } from './coverage-matrix';
import { RiskLevelSchema, type RiskLevel } from './thresholds';

export const CoverageDiffSchema = z.object({
  baseline: CoverageMatrixSchema,
  current: CoverageMatrixSchema,
  addedPersonas: z.array(z.string().min(1)),
  removedPersonas: z.array(z.string().min(1)),
  addedScenarios: z.array(z.string().min(1)),
  removedScenarios: z.array(z.string().min(1)),
  riskLevel: RiskLevelSchema
});

export type CoverageDiff = z.infer<typeof CoverageDiffSchema>;

type CoverageChangeSet = {
  addedPersonas: string[];
  removedPersonas: string[];
  addedScenarios: string[];
  removedScenarios: string[];
};

export function computeRiskLevel(changes: CoverageChangeSet): RiskLevel {
  if (changes.removedPersonas.length > 0 || changes.removedScenarios.length > 0) {
    return 'high';
  }
  const totalAdds = changes.addedPersonas.length + changes.addedScenarios.length;
  if (totalAdds > 5) {
    return 'medium';
  }
  return 'low';
}

type StateMachineLike = {
  states?: Array<{ persona?: string | null; scenario?: string | null }> | null;
};

export function diffCoverage(baseline: StateMachineLike, current: StateMachineLike): CoverageDiff {
  const baselineMatrix = buildCoverageMatrix(baseline);
  const currentMatrix = buildCoverageMatrix(current);

  const baselinePersonas = new Set(baselineMatrix.personas);
  const currentPersonas = new Set(currentMatrix.personas);
  const baselineScenarios = new Set(baselineMatrix.scenarios);
  const currentScenarios = new Set(currentMatrix.scenarios);

  const addedPersonas = currentMatrix.personas.filter((persona) => !baselinePersonas.has(persona));
  const removedPersonas = baselineMatrix.personas.filter((persona) => !currentPersonas.has(persona));
  const addedScenarios = currentMatrix.scenarios.filter((scenario) => !baselineScenarios.has(scenario));
  const removedScenarios = baselineMatrix.scenarios.filter((scenario) => !currentScenarios.has(scenario));

  const riskLevel = computeRiskLevel({
    addedPersonas,
    removedPersonas,
    addedScenarios,
    removedScenarios
  });

  return {
    baseline: baselineMatrix,
    current: currentMatrix,
    addedPersonas,
    removedPersonas,
    addedScenarios,
    removedScenarios,
    riskLevel
  };
}
